import React, { useEffect, useMemo, useState } from "react";
import { api, BACKEND_URL, formatCurrency, formatDate, formatTime, STATUS_LABELS, STATUS_CLASSES } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FilePdf, FileCsv, FileText, Student as StudentIcon, EnvelopeSimple, UsersThree, ChartBar } from "@phosphor-icons/react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell } from "recharts";
import { toast } from "sonner";

const MONTHS_TR_SHORT = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function startOfMonth(d = new Date()) { return ymd(new Date(d.getFullYear(), d.getMonth(), 1)); }
function endOfMonth(d = new Date()) { return ymd(new Date(d.getFullYear(), d.getMonth() + 1, 0)); }
function startOfYear(d = new Date()) { return ymd(new Date(d.getFullYear(), 0, 1)); }
function endOfYear(d = new Date()) { return ymd(new Date(d.getFullYear(), 11, 31)); }
function addMonths(date, n) { const d = new Date(date); d.setMonth(d.getMonth() + n); return d; }
function monthLabel(ym) {
  if (!ym || !ym.includes("-")) return ym || "";
  const [y, m] = ym.split("-");
  return `${MONTHS_TR_SHORT[Number(m) - 1]} ${y}`;
}

function downloadFromUrl(path, params, fallbackName) {
  const qs = new URLSearchParams(params).toString();
  const url = `${BACKEND_URL}/api${path}?${qs}`;
  // Use fetch with credentials so we honour httpOnly cookies, then save as blob.
  return fetch(url, { credentials: "include" }).then(async (r) => {
    if (!r.ok) {
      const txt = await r.text().catch(() => "");
      throw new Error(txt || `İndirme başarısız (HTTP ${r.status})`);
    }
    const blob = await r.blob();
    const cd = r.headers.get("content-disposition") || "";
    const m = cd.match(/filename\*=UTF-8''([^;]+)/i) || cd.match(/filename="?([^";]+)"?/i);
    let name = fallbackName;
    if (m && m[1]) {
      try { name = decodeURIComponent(m[1]); } catch { name = m[1]; }
    }
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  });
}

export default function ReportsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("student"); // student | summary
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [start, setStart] = useState(startOfMonth());
  const [end, setEnd] = useState(endOfMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendingBatch, setSendingBatch] = useState(false);

  useEffect(() => {
    api.get("/students").then((r) => {
      setStudents(r.data);
      if (r.data.length && !studentId) setStudentId(r.data[0].id);
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (start > end) return;
    setLoading(true);
    const params = { start, end };
    if (tab === "student") {
      if (!studentId) { setLoading(false); return; }
      params.student_id = studentId;
    }
    api.get("/reports/range", { params })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Rapor yüklenemedi."))
      .finally(() => setLoading(false));
  }, [tab, studentId, start, end]);

  const student = students.find((s) => s.id === studentId);
  const currency = user?.currency || "TRY";
  const totals = data?.totals || { earned: 0, paid: 0, balance: 0, expense: 0, net: 0, lesson_count: 0, completed_count: 0 };

  // Quick presets
  function applyPreset(key) {
    const today = new Date();
    if (key === "this-month") { setStart(startOfMonth(today)); setEnd(endOfMonth(today)); }
    else if (key === "last-month") { const d = addMonths(today, -1); setStart(startOfMonth(d)); setEnd(endOfMonth(d)); }
    else if (key === "last-3") { setStart(ymd(addMonths(today, -2))); setEnd(endOfMonth(today)); }
    else if (key === "last-6") { setStart(ymd(addMonths(today, -5))); setEnd(endOfMonth(today)); }
    else if (key === "this-year") { setStart(startOfYear(today)); setEnd(endOfYear(today)); }
    else if (key === "last-year") { const d = new Date(today.getFullYear() - 1, 0, 1); setStart(startOfYear(d)); setEnd(endOfYear(d)); }
  }

  async function downloadPdf() {
    if (start > end) return toast.error("Tarih aralığı geçersiz.");
    setBusy(true);
    try {
      const params = { start, end };
      const fallback = `rapor_${start}_${end}.pdf`;
      if (tab === "student" && studentId) params.student_id = studentId;
      await downloadFromUrl("/reports/range/pdf", params, fallback);
      toast.success("PDF indirildi.");
    } catch (e) { toast.error(e.message || "PDF oluşturulamadı."); }
    finally { setBusy(false); }
  }

  async function downloadCsv() {
    if (start > end) return toast.error("Tarih aralığı geçersiz.");
    setBusy(true);
    try {
      const params = { start, end };
      const fallback = `rapor_${start}_${end}.csv`;
      if (tab === "student" && studentId) params.student_id = studentId;
      await downloadFromUrl("/reports/range/csv", params, fallback);
      toast.success("CSV indirildi.");
    } catch (e) { toast.error(e.message || "CSV oluşturulamadı."); }
    finally { setBusy(false); }
  }

  // Email — uses existing month-based endpoint; we send for the selected END month.
  async function sendToParent() {
    if (!studentId) return toast.error("Öğrenci seçin.");
    if (!student?.parent_email) return toast.error("Bu öğrencinin veli e-postası tanımlı değil.");
    const d = new Date(end + "T00:00:00");
    setSending(true);
    try {
      const r = await api.post("/reports/send-email", { student_id: studentId, year: d.getFullYear(), month: d.getMonth() + 1 });
      if (r.data?.sent > 0) toast.success(`Rapor ${student.parent_email} adresine gönderildi (${MONTHS_TR_SHORT[d.getMonth()]} ${d.getFullYear()}).`);
      else toast.error(r.data?.details?.[0]?.reason || "Gönderilemedi.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gönderilemedi."); }
    finally { setSending(false); }
  }

  async function sendToAllParents() {
    const d = new Date(end + "T00:00:00");
    if (!window.confirm(`${MONTHS_TR_SHORT[d.getMonth()]} ${d.getFullYear()} raporu tüm velilere gönderilsin mi?`)) return;
    setSendingBatch(true);
    try {
      const r = await api.post("/reports/send-email", { year: d.getFullYear(), month: d.getMonth() + 1 });
      const sent = r.data?.sent || 0;
      const skipped = r.data?.skipped || 0;
      if (sent > 0) toast.success(`${sent} veliye rapor gönderildi${skipped ? ` · ${skipped} atlandı` : ""}.`);
      else toast.error("Hiç rapor gönderilemedi. Resend API anahtarı ve veli e-postalarını kontrol edin.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gönderilemedi."); }
    finally { setSendingBatch(false); }
  }

  const chartMonthly = useMemo(() => (data?.monthly || []).map((m) => ({ ...m, label: monthLabel(m.month) })), [data]);
  const chartByStudent = useMemo(() => (data?.by_student || []).slice(0, 8).map((s) => ({ name: (s.name || "").slice(0, 14), earned: s.earned, paid: s.paid, color: s.color || "#3A5A40" })), [data]);

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Raporlar</h1>
        <p className="text-muted-foreground text-sm mt-1">Tarih aralığı seçerek öğrenci bazlı veya toplu rapor oluşturun.</p>
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-border rounded-lg p-5 space-y-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="student" data-testid="reports-tab-student"><StudentIcon size={14} weight="duotone" className="mr-1.5" /> Öğrenci bazlı</TabsTrigger>
            <TabsTrigger value="summary" data-testid="reports-tab-summary"><UsersThree size={14} weight="duotone" className="mr-1.5" /> Toplu özet</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="grid md:grid-cols-4 gap-4">
          {tab === "student" && (
            <div className="space-y-1.5 md:col-span-2">
              <Label>Öğrenci</Label>
              <Select value={studentId} onValueChange={setStudentId}>
                <SelectTrigger data-testid="reports-student-select"><SelectValue placeholder="Öğrenci seçin" /></SelectTrigger>
                <SelectContent>
                  {students.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-muted-foreground">Önce öğrenci ekleyin</div>
                  ) : students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Başlangıç</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="reports-start-date" />
          </div>
          <div className="space-y-1.5">
            <Label>Bitiş</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="reports-end-date" />
          </div>
          {tab === "summary" && <div className="hidden md:block md:col-span-2" />}
        </div>

        <div className="flex flex-wrap gap-2">
          <span className="text-xs text-muted-foreground self-center mr-1">Hızlı seçim:</span>
          <PresetBtn onClick={() => applyPreset("this-month")} testId="preset-this-month">Bu ay</PresetBtn>
          <PresetBtn onClick={() => applyPreset("last-month")} testId="preset-last-month">Geçen ay</PresetBtn>
          <PresetBtn onClick={() => applyPreset("last-3")} testId="preset-last-3">Son 3 ay</PresetBtn>
          <PresetBtn onClick={() => applyPreset("last-6")} testId="preset-last-6">Son 6 ay</PresetBtn>
          <PresetBtn onClick={() => applyPreset("this-year")} testId="preset-this-year">Bu yıl</PresetBtn>
          <PresetBtn onClick={() => applyPreset("last-year")} testId="preset-last-year">Geçen yıl</PresetBtn>
        </div>

        <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-border">
          {tab === "student" && (
            <>
              <Button onClick={sendToAllParents} disabled={sendingBatch || students.length === 0} variant="outline" data-testid="reports-send-all-button">
                <UsersThree size={16} className="mr-1" weight="duotone" />
                {sendingBatch ? "Gönderiliyor…" : "Tüm velilere gönder"}
              </Button>
              <Button onClick={sendToParent} disabled={!studentId || sending} variant="outline" data-testid="reports-send-button">
                <EnvelopeSimple size={16} className="mr-1" weight="duotone" />
                {sending ? "Gönderiliyor…" : "Veliye e-posta"}
              </Button>
            </>
          )}
          <Button onClick={downloadCsv} disabled={busy || (tab === "student" && !studentId)} variant="outline" data-testid="reports-csv-button">
            <FileCsv size={16} className="mr-1" weight="duotone" /> CSV
          </Button>
          <Button onClick={downloadPdf} disabled={busy || (tab === "student" && !studentId)} className="bg-[#D97757] hover:bg-[#BF6446] text-white" data-testid="reports-pdf-button">
            <FilePdf size={16} className="mr-1" weight="duotone" /> {busy ? "Hazırlanıyor…" : "PDF olarak indir"}
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatBox label="Toplam ders" value={totals.lesson_count} testId="stat-lessons" />
        <StatBox label="Tamamlanan" value={totals.completed_count} tone="success" testId="stat-completed" />
        <StatBox label="Hak edilen" value={formatCurrency(totals.earned, currency)} tone="primary" testId="stat-earned" />
        <StatBox label={tab === "student" ? "Kalan bakiye" : "Tahsil edilen"} value={formatCurrency(tab === "student" ? totals.balance : totals.paid, currency)} tone={tab === "student" && totals.balance > 0 ? "accent" : "success"} testId="stat-balance-or-paid" />
      </div>

      {/* Charts */}
      {chartMonthly.length > 0 && (
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="bg-white border border-border rounded-lg p-5">
            <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><ChartBar size={18} weight="duotone" className="text-[#3A5A40]" /> Aylık trend</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartMonthly}>
                  <CartesianGrid stroke="#E5E4E0" vertical={false} />
                  <XAxis dataKey="label" stroke="#5C6A5D" fontSize={12} />
                  <YAxis stroke="#5C6A5D" fontSize={12} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E5E4E0", borderRadius: 8 }} formatter={(v) => formatCurrency(v, currency)} />
                  <Legend />
                  <Line type="monotone" dataKey="earned" stroke="#3A5A40" strokeWidth={2.5} dot={{ r: 3 }} name="Hak edilen" />
                  <Line type="monotone" dataKey="paid" stroke="#2E7D32" strokeWidth={2} dot={{ r: 3 }} name="Tahsil edilen" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          {tab === "summary" && chartByStudent.length > 0 && (
            <div className="bg-white border border-border rounded-lg p-5">
              <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><UsersThree size={18} weight="duotone" className="text-[#3A5A40]" /> Öğrenci kazançları</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartByStudent} layout="vertical" margin={{ left: 8, right: 8 }}>
                    <CartesianGrid stroke="#E5E4E0" horizontal={false} />
                    <XAxis type="number" stroke="#5C6A5D" fontSize={12} />
                    <YAxis dataKey="name" type="category" stroke="#5C6A5D" fontSize={12} width={100} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E5E4E0", borderRadius: 8 }} formatter={(v) => formatCurrency(v, currency)} />
                    <Bar dataKey="earned" name="Hak edilen" radius={[0, 4, 4, 0]}>
                      {chartByStudent.map((s, i) => <Cell key={i} fill={s.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Detail panel */}
      {tab === "student" ? (
        <StudentDetail data={data} loading={loading} currency={currency} />
      ) : (
        <SummaryTable data={data} loading={loading} currency={currency} />
      )}
    </div>
  );
}

function PresetBtn({ children, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="text-xs px-3 py-1 rounded-full border border-border bg-white hover:bg-[#FAF9F6] hover:border-[#3A5A40] hover:text-[#3A5A40] transition-colors"
    >
      {children}
    </button>
  );
}

function StatBox({ label, value, tone = "neutral", testId }) {
  const toneCls = {
    primary: "text-[#3A5A40]",
    success: "text-[#2E7D32]",
    accent: "text-[#D97757]",
    neutral: "text-foreground",
  }[tone];
  return (
    <div className="bg-white border border-border rounded-lg p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`font-display text-xl md:text-2xl font-bold mt-1 ${toneCls}`}>{value}</div>
    </div>
  );
}

function StudentDetail({ data, loading, currency }) {
  const lessons = data?.lessons || [];
  const payments = data?.payments || [];
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div className="bg-white border border-border rounded-lg p-5">
        <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2">
          <FileText size={18} weight="duotone" className="text-[#3A5A40]" /> Dersler
        </h2>
        {loading ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Yükleniyor…</div>
        ) : lessons.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Bu aralıkta ders yok.</div>
        ) : (
          <ul className="divide-y divide-border max-h-96 overflow-y-auto -mx-2" data-testid="report-lessons-list">
            {lessons.map((l) => {
              const amount = (l.status === "completed" || l.status === "cancelled_paid") ? l.amount : 0;
              return (
                <li key={l.id} className="flex items-center gap-3 px-2 py-2.5 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{formatDate(l.start_time)} · {formatTime(l.start_time)}–{formatTime(l.end_time)}</div>
                    <div className="text-xs text-muted-foreground truncate">{l.subject || <span className="italic">konu girilmemiş</span>}</div>
                  </div>
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold border ${STATUS_CLASSES[l.status]}`}>
                    {STATUS_LABELS[l.status]}
                  </span>
                  <div className="font-mono text-sm w-20 text-right">{formatCurrency(amount, currency)}</div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <div className="bg-white border border-border rounded-lg p-5">
        <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2">
          <FileText size={18} weight="duotone" className="text-[#D97757]" /> Ödemeler
        </h2>
        {payments.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Bu aralıkta ödeme kaydı yok.</div>
        ) : (
          <ul className="divide-y divide-border max-h-96 overflow-y-auto -mx-2" data-testid="report-payments-list">
            {payments.map((p) => (
              <li key={p.id} className="flex items-center gap-3 px-2 py-2.5 text-sm">
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{formatDate(p.date)} · {p.method}</div>
                  {p.note && <div className="text-xs text-muted-foreground truncate">{p.note}</div>}
                </div>
                <div className="font-mono text-sm text-[#2E7D32] font-semibold">{formatCurrency(p.amount, currency)}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function SummaryTable({ data, loading, currency }) {
  const rows = data?.by_student || [];
  return (
    <div className="bg-white border border-border rounded-lg overflow-hidden">
      <h2 className="font-display font-bold text-lg p-5 pb-2 flex items-center gap-2">
        <UsersThree size={18} weight="duotone" className="text-[#3A5A40]" /> Öğrenci özetleri
      </h2>
      {loading ? (
        <div className="text-sm text-muted-foreground py-12 text-center">Yükleniyor…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-muted-foreground py-12 text-center">Bu aralıkta veri yok.</div>
      ) : (
        <>
          {/* Desktop */}
          <table className="w-full text-sm hidden md:table" data-testid="report-summary-table">
            <thead className="bg-muted/40 text-xs text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2">Öğrenci</th>
                <th className="text-right px-4 py-2">Ders</th>
                <th className="text-right px-4 py-2">Tamamlanan</th>
                <th className="text-right px-4 py-2">Hak edilen</th>
                <th className="text-right px-4 py-2">Tahsil edilen</th>
                <th className="text-right px-4 py-2">Bakiye</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr key={r.student_id} data-testid={`summary-row-${r.student_id}`}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ background: r.color }} />
                      <span className="font-medium">{r.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono">{r.lesson_count}</td>
                  <td className="px-4 py-2.5 text-right font-mono">{r.completed}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-[#3A5A40] font-semibold">{formatCurrency(r.earned, currency)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-[#2E7D32]">{formatCurrency(r.paid, currency)}</td>
                  <td className={`px-4 py-2.5 text-right font-mono font-semibold ${r.balance > 0 ? "text-[#D97757]" : "text-[#2E7D32]"}`}>{formatCurrency(r.balance, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Mobile cards */}
          <ul className="md:hidden divide-y divide-border">
            {rows.map((r) => (
              <li key={r.student_id} className="p-4 space-y-1.5" data-testid={`summary-card-${r.student_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: r.color }} />
                    <span className="font-medium truncate">{r.name}</span>
                  </div>
                  <span className="font-mono text-sm text-[#3A5A40] font-semibold whitespace-nowrap">{formatCurrency(r.earned, currency)}</span>
                </div>
                <div className="text-xs text-muted-foreground flex flex-wrap gap-3">
                  <span>{r.lesson_count} ders · {r.completed} tamamlandı</span>
                  <span>Tahsil: {formatCurrency(r.paid, currency)}</span>
                  <span className={r.balance > 0 ? "text-[#D97757]" : "text-[#2E7D32]"}>Bakiye: {formatCurrency(r.balance, currency)}</span>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
